import { useState, useCallback, useRef, useEffect } from "react";
import Cropper from "react-easy-crop";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";
import { ZoomIn, ZoomOut, Check, X, RotateCcw, Move } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Enhanced image crop editor with direct manipulation.
 * @param {string} title - Dialog title
 * @param {number} aspect - Aspect ratio (1 = square, 16/9 = widescreen)
 * @param {"round"|"rect"} cropShape - Crop shape
 * @param {string} containerClass - CSS class for the crop container aspect ratio (e.g. aspect-square, aspect-video)
 */
export default function ImageCropEditor({
  open, onClose, imageUrl, initialCrop, onSave, saving,
  title = "Adjust Image",
  aspect = 1,
  cropShape = "round",
  containerClass = "aspect-square",
  hint = "Drag to reposition. Pinch or scroll to zoom.",
}) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(initialCrop?.zoom || 1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);

  // Reset state when dialog opens with new image
  useEffect(() => {
    if (open) {
      setCrop({ x: initialCrop?.cropX || 0, y: initialCrop?.cropY || 0 });
      setZoom(initialCrop?.zoom || 1);
    }
  }, [open, initialCrop]);

  const onCropComplete = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleSave = () => {
    if (croppedAreaPixels) {
      onSave({
        x: croppedAreaPixels.x,
        y: croppedAreaPixels.y,
        width: croppedAreaPixels.width,
        height: croppedAreaPixels.height,
        zoom,
        cropX: crop.x,
        cropY: crop.y,
      });
    }
  };

  const handleReset = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  const handleZoomIn = () => setZoom(Math.min(4, zoom + 0.25));
  const handleZoomOut = () => setZoom(Math.max(1, zoom - 0.25));

  if (!imageUrl) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !saving) onClose(); }}>
      <DialogContent
        className={cn(
          "flex max-h-[90vh] w-[calc(100vw-1.5rem)] max-w-lg flex-col gap-0 overflow-hidden p-0 sm:w-full",
        )}
        data-testid="image-crop-dialog"
      >
        <DialogHeader className="shrink-0 space-y-0 px-5 pb-3 pt-5 pr-14 text-left">
          <DialogTitle className="text-base flex items-center gap-2">
            {title}
            {isDragging && (
              <span className="text-xs font-normal text-primary animate-pulse flex items-center gap-1">
                <Move size={12} /> Moving...
              </span>
            )}
          </DialogTitle>
          <p className="text-xs text-muted-foreground mt-1">{hint}</p>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 pb-3 [scrollbar-gutter:stable]">
          <div className="space-y-4">
            <div
              ref={containerRef}
              className={cn(
                "relative w-full max-w-full bg-black/95 rounded-2xl overflow-hidden border-2 transition-all duration-200",
                isDragging ? "border-primary shadow-lg shadow-primary/20" : "border-border/30",
                containerClass,
              )}
              style={{
                minHeight: "320px",
                maxHeight: "min(52vh, calc(90vh - 15rem))",
              }}
              data-testid="crop-area"
            >
              <Cropper
                image={imageUrl}
                crop={crop}
                zoom={zoom}
                aspect={aspect}
                cropShape={cropShape}
                showGrid={cropShape === "rect"}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
                onInteractionStart={() => setIsDragging(true)}
                onInteractionEnd={() => setIsDragging(false)}
                minZoom={1}
                maxZoom={4}
                zoomSpeed={0.2}
                restrictPosition={true}
                mediaProps={{
                  style: { objectFit: "contain" },
                }}
                style={{
                  containerStyle: {
                    borderRadius: "16px",
                    cursor: isDragging ? "grabbing" : "grab",
                  },
                  cropAreaStyle: {
                    border: `3px solid ${isDragging ? "rgb(var(--primary))" : "rgba(255,255,255,0.5)"}`,
                    boxShadow: isDragging
                      ? "0 0 0 4px rgba(99,102,241,0.3), 0 0 30px rgba(99,102,241,0.15)"
                      : "0 0 0 2px rgba(255,255,255,0.1)",
                    transition: "border-color 0.2s, box-shadow 0.2s",
                  },
                  mediaStyle: {
                    transition: "transform 0.1s ease-out",
                  },
                }}
              />

            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 pointer-events-none">
              <div
                className={cn(
                  "flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] font-medium transition-all duration-300",
                  isDragging
                    ? "bg-primary text-primary-foreground"
                    : "bg-black/60 text-white/80 backdrop-blur-sm",
                )}
              >
                <Move size={12} />
                {isDragging ? "Release to set position" : "Drag image to reposition"}
              </div>
            </div>
            </div>

            <div className="bg-secondary/30 rounded-xl border border-border/30 p-3" data-testid="zoom-controls">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleZoomOut}
                  disabled={zoom <= 1}
                  className="shrink-0 rounded-lg border border-border bg-background p-2 transition-all hover:bg-secondary disabled:cursor-not-allowed disabled:opacity-40 active:scale-95"
                  data-testid="zoom-out-btn"
                >
                  <ZoomOut size={18} />
                </button>

                <div className="flex flex-1 items-center gap-3">
                  <Slider
                    value={[zoom]}
                    min={1}
                    max={4}
                    step={0.05}
                    onValueChange={([v]) => setZoom(v)}
                    className="flex-1"
                    data-testid="zoom-slider"
                  />
                </div>

                <button
                  type="button"
                  onClick={handleZoomIn}
                  disabled={zoom >= 4}
                  className="shrink-0 rounded-lg border border-border bg-background p-2 transition-all hover:bg-secondary disabled:cursor-not-allowed disabled:opacity-40 active:scale-95"
                  data-testid="zoom-in-btn"
                >
                  <ZoomIn size={18} />
                </button>

                <span className="w-12 shrink-0 text-center font-mono text-sm text-muted-foreground">
                  {zoom.toFixed(1)}x
                </span>
              </div>
            </div>

            <p className="text-center text-[10px] text-muted-foreground">{hint}</p>
          </div>
        </div>

        <div
          className="shrink-0 border-t border-border bg-background px-5 py-4 shadow-[0_-8px_24px_-12px_rgba(0,0,0,0.15)]"
          data-testid="crop-dialog-footer"
        >
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleReset} className="gap-1.5" data-testid="crop-reset-btn">
              <RotateCcw size={14} /> Reset
            </Button>
            <div className="flex-1" />
            <Button variant="outline" size="sm" onClick={onClose} disabled={saving} className="gap-1.5" data-testid="crop-cancel-btn">
              <X size={14} /> Cancel
            </Button>
            <Button size="sm" onClick={handleSave} disabled={saving} className="gap-1.5 btn-glow" data-testid="crop-save-btn">
              <Check size={14} /> {saving ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
